import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Globe, Wand2, RefreshCw, Link as LinkIcon } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function UrlToAppleDialog({ open, onOpenChange, onBlueprint }) {
  const [url, setUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUrl("");
    setNotes("");
  }, [open]);

  const submit = async () => {
    if (!url.trim()) return toast.error("Paste a URL");
    setBusy(true);
    try {
      const { data } = await api.post("/prompt/from-url", {
        url: url.trim(),
        extra_notes: notes.trim(),
      });
      toast.success("Apple PWA blueprint ready");
      onBlueprint?.(data);
      onOpenChange(false);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error(err.response?.data?.detail || "You've used all your prompts this month.");
      } else {
        toast.error(err.response?.data?.detail || "Could not convert that URL.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream border-2 border-ink rounded-3xl max-w-2xl" data-testid="url-to-apple-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
            <Globe className="w-5 h-5 text-marigold" /> URL → Apple PWA app
          </DialogTitle>
          <DialogDescription className="text-ink2">
            Paste any web app or PWA. We'll wrap it in a polished SwiftUI <code className="text-marigold font-mono">WKWebView</code> shell
            with native navigation, pull-to-refresh, and share-sheet — plus 1–2 fully-native screens for the most
            important feature so it feels at home on the App Store.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div>
            <div className="label-eyebrow mb-2">Site URL</div>
            <div className="flex items-center gap-2 card-soft px-3 py-2">
              <LinkIcon className="w-4 h-4 text-ink2 flex-shrink-0" />
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                className="w-full bg-transparent outline-none text-sm"
                data-testid="url-input"
              />
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-2">Native screens to focus on (optional)</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={400}
              placeholder="e.g. native widgets for the dashboard; reuse the WebView for everything else."
              className="w-full card-soft p-3 text-sm bg-transparent outline-none resize-none"
              data-testid="url-notes-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button
              onClick={() => onOpenChange(false)}
              className="text-sm px-4 py-2 rounded-full border border-border text-ink2 hover:border-ink hover:text-ink"
              data-testid="url-import-cancel"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={busy}
              className="btn-primary text-sm py-2 disabled:opacity-40"
              data-testid="url-import-submit"
            >
              {busy ? <><RefreshCw className="w-4 h-4 animate-spin" /> Wrapping…</> : <><Wand2 className="w-4 h-4" /> Wrap as Apple app</>}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
